import { create } from 'zustand';

import { CurrentUser, currentUserApi, PremiumFlag } from '../axios';
// import { SSOApi } from '../axios/sso';
import { LangParam } from '../types';
import { clearAmplitudeIdentity, setAmplitudeIdentity } from '../utils/eventTracker';
import { shutdownIntercom } from '../utils/intercom';
import { useNestedLocationStore } from './nestedLocationStore';

interface UserDataStore {
  permissions: string[];
  featureFlags: string[];
  premiumFlags: Record<string, PremiumFlag>;
  loading: boolean;
  currentUser: CurrentUser | null;
  lang: LangParam;
  fetchedUserData: boolean;
  premiumFeatures: Record<string, string>;

  // Permission checking methods
  hasAdminRoleOnLocations: () => boolean;

  actions: {
    initializeUserData: () => Promise<void>;
    refreshPremiumFlags: () => Promise<void>;
    setLang: (lang: LangParam) => void;
    logoutUser: () => void;
    addFreeFormAnswer: (formAnswerId: number) => void;
  };
}

const useUserDataStore = create<UserDataStore>((set, get) => ({
  permissions: [],
  featureFlags: [],
  premiumFlags: {},
  loading: false,
  currentUser: null,
  lang: 'en',
  fetchedUserData: false,
  premiumFeatures: {},
  hasAdminRoleOnLocations: () => {
    const { currentUser, permissions } = get();
    return !!(
      currentUser?.is_admin ||
      currentUser?.is_superadmin ||
      currentUser?.is_support ||
      permissions?.includes('admin_places')
    );
  },

  actions: {
    initializeUserData: async () => {
      if (get().loading) return;
      try {
        set({ loading: true });
        const [permissions, flagsResponse, user] = await Promise.all([
          currentUserApi.getCurrentUserPermissions(),
          currentUserApi.getCurrentUserFlags(),
          currentUserApi.getCurrentUserName(),
        ]);

        set((state) => ({
          permissions,
          featureFlags: flagsResponse.flags,
          premiumFlags: flagsResponse.premium_flags || {},
          premiumFeatures: flagsResponse.premium_features || {},
          currentUser: user,
          loading: false,
          lang: user?.lang || state.lang,
        }));

        // Set Amplitude identity for user and account grouping
        if (user?.id && user?.company_id) {
          setAmplitudeIdentity(user.id, user.company_id);
        }

        if (flagsResponse?.flags?.includes('NestableLocations')) {
          await useNestedLocationStore.getState().actions.loadAllLocations();
        }

        currentUserApi.syncCompanyDataToIntercom();
      } catch (error) {
        console.error('Failed to initialize user data:', error);
        set({ loading: false });
      } finally {
        set({ fetchedUserData: true });
      }
    },

    refreshPremiumFlags: async () => {
      const flagsResponse = await currentUserApi.getCurrentUserFlags();
      if (Object.keys(flagsResponse.premium_flags).length > 0) {
        set({ premiumFlags: flagsResponse.premium_flags || {} });
      }
    },

    setLang: (lang: LangParam) => set({ lang }),

    logoutUser: () => {
      // try {
      //   // Try to logout from SSO as well
      //   await SSOApi.logoutSSO();
      // } catch (error) {
      //   console.error('SSO logout failed:', error);
      // }

      set({ currentUser: null, permissions: [], featureFlags: [], premiumFlags: {} });
      clearAmplitudeIdentity();
      shutdownIntercom();
    },

    addFreeFormAnswer: (formAnswerId: number) => {
      if (
        get().currentUser?.free_form_answers?.length &&
        get().currentUser?.free_form_answers?.length >= 99
      )
        return;
      set((state) => ({
        currentUser: {
          ...state.currentUser,
          free_form_answers: [...(state.currentUser?.free_form_answers || []), formAnswerId],
        },
      }));
    },
  },
}));

export { useUserDataStore };
